import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCategories } from "@/hooks/useCategories";
import { useProducts } from "@/hooks/useProducts";
import { useProductionLocations } from "@/hooks/useProductionLocations";
import { ComboSlot, ComboSlotFormData, PricingRule, SlotType } from "@/hooks/useComboSlots";
import { Loader2 } from "lucide-react";

const formSchema = z.object({
  slot_type: z.enum(['category', 'product']),
  category_id: z.string().optional(),
  product_id: z.string().optional(),
  min_quantity: z.coerce.number().min(0),
  max_quantity: z.coerce.number().min(1),
  pricing_rule: z.enum(['highest', 'average', 'fixed', 'rodizio']),
  fixed_price: z.coerce.number().min(0).optional(),
  production_location_id: z.string().optional(),
  label: z.string().optional(),
  is_required: z.boolean(),
}).refine((data) => {
  if (data.slot_type === 'category' && !data.category_id) {
    return false;
  }
  if (data.slot_type === 'product' && !data.product_id) {
    return false;
  }
  return true;
}, {
  message: "Selecione uma categoria ou produto",
  path: ["category_id"],
}).refine((data) => data.max_quantity >= data.min_quantity, {
  message: "Máximo deve ser maior ou igual ao mínimo",
  path: ["max_quantity"],
});

type FormData = z.infer<typeof formSchema>;

interface ComboSlotFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slot?: ComboSlot | null;
  onSubmit: (data: ComboSlotFormData) => Promise<void>;
  isPending?: boolean;
}

const PRICING_RULES: { value: PricingRule; label: string; description: string }[] = [
  { value: 'highest', label: 'Maior Preço', description: 'Cobra o maior preço entre os selecionados' },
  { value: 'average', label: 'Média', description: 'Cobra a média dos preços selecionados' },
  { value: 'fixed', label: 'Preço Fixo', description: 'Cobra um valor fixo definido' },
  { value: 'rodizio', label: 'Rodízio (R$ 0)', description: 'Não cobra por este slot (liberado)' },
];

export function ComboSlotFormDialog({
  open,
  onOpenChange,
  slot,
  onSubmit,
  isPending,
}: ComboSlotFormDialogProps) {
  const { categories, fetchCategories } = useCategories();
  const { products, fetchProducts } = useProducts();
  const { locations, fetchLocations } = useProductionLocations();

  useEffect(() => {
    if (open) {
      fetchCategories();
      fetchProducts();
      fetchLocations();
    }
  }, [open, fetchCategories, fetchProducts, fetchLocations]);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      slot_type: 'category',
      category_id: "",
      product_id: "",
      min_quantity: 1,
      max_quantity: 1,
      pricing_rule: 'highest',
      fixed_price: 0,
      production_location_id: "",
      label: "",
      is_required: true,
    },
  });

  const slotType = form.watch('slot_type');
  const pricingRule = form.watch('pricing_rule');

  useEffect(() => {
    if (slot) {
      form.reset({
        slot_type: slot.slot_type as SlotType,
        category_id: slot.category_id || "",
        product_id: slot.product_id || "",
        min_quantity: slot.min_quantity,
        max_quantity: slot.max_quantity,
        pricing_rule: slot.pricing_rule as PricingRule,
        fixed_price: Number(slot.fixed_price) || 0,
        production_location_id: slot.production_location_id || "",
        label: slot.label || "",
        is_required: slot.is_required,
      });
    } else {
      form.reset({
        slot_type: 'category',
        category_id: "",
        product_id: "",
        min_quantity: 1,
        max_quantity: 1,
        pricing_rule: 'highest',
        fixed_price: 0,
        production_location_id: "",
        label: "",
        is_required: true,
      });
    }
  }, [slot, form, open]);

  const handleSubmit = async (data: FormData) => {
    await onSubmit({
      slot_type: data.slot_type,
      category_id: data.slot_type === 'category' ? data.category_id : null,
      product_id: data.slot_type === 'product' ? data.product_id : null,
      min_quantity: data.min_quantity,
      max_quantity: data.max_quantity,
      pricing_rule: data.pricing_rule,
      fixed_price: data.pricing_rule === 'fixed' ? data.fixed_price : 0,
      production_location_id: data.production_location_id || null,
      label: data.label || null,
      is_required: data.is_required,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {slot ? "Editar Slot" : "Adicionar Slot"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome do Slot (opcional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Escolha sua bebida" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="slot_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de Seleção</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="category">Classe (Categoria)</SelectItem>
                      <SelectItem value="product">Material (Produto)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription className="text-xs">
                    {slotType === 'category'
                      ? 'Cliente pode escolher qualquer produto desta categoria'
                      : 'Cliente recebe este produto específico'}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {slotType === 'category' && (
              <FormField
                control={form.control}
                name="category_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Categoria</FormLabel>
                    <Select value={field.value || ""} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione uma categoria" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {slotType === 'product' && (
              <FormField
                control={form.control}
                name="product_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Produto</FormLabel>
                    <Select value={field.value || ""} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um produto" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {products.map((prod) => (
                          <SelectItem key={prod.id} value={prod.id}>
                            {prod.name} - R$ {Number(prod.price).toFixed(2)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="min_quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Qtd. Mínima</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="max_quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Qtd. Máxima</FormLabel>
                    <FormControl>
                      <Input type="number" min="1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="pricing_rule"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Precificador</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {PRICING_RULES.map((rule) => (
                        <SelectItem key={rule.value} value={rule.value}>
                          {rule.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription className="text-xs">
                    {PRICING_RULES.find(r => r.value === pricingRule)?.description}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {pricingRule === 'fixed' && (
              <FormField
                control={form.control}
                name="fixed_price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Preço Fixo (R$)</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" min="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="production_location_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Local de Produção</FormLabel>
                  <Select
                    value={field.value || "none"}
                    onValueChange={(v) => field.onChange(v === "none" ? "" : v)}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Herdar do combo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">Herdar do combo/produto</SelectItem>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_required"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <FormLabel className="font-medium">Obrigatório</FormLabel>
                    <FormDescription className="text-xs">
                      Cliente deve fazer seleção neste slot
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {slot ? "Salvar" : "Adicionar"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
